import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OrderCancellationReason } from '@hero/schema';
import { Modal } from '../../../app/Modal';
import { Button } from '../../../ui/Button';
import { Textarea } from '../../../ui/Textarea';
import { SelectAuto, type SelectAutoOption } from '../../../app/Select/SelectAuto';

export interface OrderCancelOption {
  reason: OrderCancellationReason;
  requriedNote?: boolean;
  target: ('customer' | 'admin' | 'carrier')[];
}

export interface OrderCancelModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Fires after the user picks a reason (and note if required) and
   * confirms. Parent owns the actual cancel-order mutation; the modal
   * stays open while `isCanceling` is `true` so the user can't double
   * submit.
   */
  onConfirm: (reason: OrderCancellationReason, internalNote?: string) => void;
  isCanceling?: boolean;
  /** Pre-filtered list of reasons valid for the *current* role. */
  cancelOptions: readonly OrderCancelOption[];
}

/**
 * Shared cancel-order modal. Mounts inside `OrderActions` for both
 * customer and carrier; the role decides which `cancelOptions` flow
 * through (see `useOrderCancelOptions` → `customerOptions` /
 * `carrierOptions`). Translations come from the shared `m-order`
 * namespace so adding a new role doesn't fan out to per-app locale
 * files.
 */
export const OrderCancelModal: React.FC<OrderCancelModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isCanceling = false,
  cancelOptions,
}) => {
  const { t } = useTranslation(['m-order', 'c-main']);
  const [reason, setReason] = useState<OrderCancellationReason | ''>('');
  const [internalNote, setInternalNote] = useState<string>('');

  const selectedReasonConfig = cancelOptions.find(opt => opt.reason === reason);
  const isNoteRequired = selectedReasonConfig?.requriedNote ?? false;

  const handleConfirm = () => {
    if (!reason) return;
    if (isNoteRequired && !internalNote.trim()) return;
    onConfirm(reason, internalNote.trim() || undefined);
    setReason('');
    setInternalNote('');
  };

  const handleCancel = () => {
    if (isCanceling) return;
    setReason('');
    setInternalNote('');
    onClose();
  };

  const isConfirmDisabled = !reason || (isNoteRequired && !internalNote.trim()) || isCanceling;

  const selectOptions: SelectAutoOption[] = cancelOptions.map(option => ({
    value: option.reason,
    label: t(`m-order:actions.cancellation_reasons.${option.reason}`, {
      defaultValue: String(option.reason),
    }),
    description: t(`m-order:actions.cancellation_reasons.${option.reason}_description`, {
      defaultValue: '',
    }),
  }));

  return (
    <Modal
      btnTitle=""
      isOpen={isOpen}
      onClose={handleCancel}
      modalTitle={t('m-order:actions.cancel_order')}>
      <div className="space-y-4">
        <p className="text-sm text-gray-600">{t('m-order:actions.cancel_order_confirmation')}</p>

        <SelectAuto
          options={selectOptions}
          value={reason}
          onChange={value => setReason((value as OrderCancellationReason) || '')}
          placeholder={t('m-order:actions.select_cancellation_reason')}
          label={t('m-order:actions.cancellation_reason')}
          required
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('m-order:actions.cancellation_note')}
            {isNoteRequired && <span className="text-red-500 ms-1">*</span>}
          </label>
          <Textarea
            value={internalNote}
            onChange={e => setInternalNote(e.target.value)}
            placeholder={
              isNoteRequired
                ? t('m-order:actions.cancellation_note_required')
                : t('m-order:actions.cancellation_note_placeholder')
            }
            className="min-h-[100px]"
            required={isNoteRequired}
          />
          {isNoteRequired && !internalNote.trim() && (
            <p className="text-sm text-red-500 mt-1">
              {t('m-order:actions.cancellation_note_required')}
            </p>
          )}
        </div>

        <div className="flex gap-2 pt-4 justify-end rtl:flex-row-reverse">
          <Button type="button" variant="outline" onClick={handleCancel} disabled={isCanceling}>
            {t('c-main:crud_buttons.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={isConfirmDisabled}>
            {isCanceling
              ? t('m-order:actions.canceling')
              : t('m-order:actions.confirm_cancel')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
